import type { Section, StationStatus, StationType } from '../../lib/types'

const STATION_TYPES: ReadonlyArray<StationType> = ['image', 'clinical', 'instrument', 'mixed']
const STATION_STATUSES: ReadonlyArray<StationType | StationStatus> = ['draft', 'needs_review', 'published', 'archived']

export interface ApiListQuery {
  search?: string
  sectionId?: string
  status?: StationStatus | 'all'
  page?: number
  pageSize?: number
  includeArchived?: boolean
}

export interface AdminApiOptions {
  /** Firebase's getIdToken is async, while a synchronous provider is useful in tests. */
  getToken: () => string | null | Promise<string | null>
}

export type AdminStationType = StationType | (string & {})

export interface AdminMedia {
  id: string
  stationId: string
  key: string
  filename: string | null
  mimeType?: string
  sizeBytes?: number | null
  altText: string
  alt?: string
  sortOrder: number
  url?: string
}

export interface AdminAnswer {
  id: string
  subquestionId: string
  body: string
  order: number
  sortOrder: number
  isPrimary: boolean
}

export interface AdminSubquestion {
  id: string
  stationId: string
  prompt: string
  questionType: string
  order: number
  sortOrder: number
  marks?: number | null
  answers: AdminAnswer[]
}

export interface AdminStation {
  id: string
  sectionId: string
  sectionName?: string
  sectionSlug?: string
  title: string
  scenario: string
  type: AdminStationType
  source: string | null
  sourcePages: string | string[] | null
  sortOrder: number
  status: StationStatus
  tags: string[]
  order: number
  questionCount?: number
  mediaCount?: number
  createdAt?: string | null
  updatedAt?: string | null
  deletedAt?: string | null
  images: AdminMedia[]
  subquestions: AdminSubquestion[]
}

export interface AdminStationSummary {
  id: string
  sectionId: string
  sectionName?: string
  sectionSlug?: string
  title: string
  scenario?: string
  type: AdminStationType
  source?: string | null
  sourcePages?: string | string[] | null
  sortOrder: number
  order?: number
  status: StationStatus
  tags: string[]
  questionCount?: number
  mediaCount?: number
  images?: AdminMedia[]
}

export interface AdminStationListResult {
  stations: AdminStationSummary[]
  total: number
  page: number
  pageSize: number
  hasMore: boolean
}

export interface AdminSectionResult {
  sections: Section[]
}

export interface CreateStationInput {
  sectionId: string
  title: string
  scenario: string
  type: string
  source?: string
  sourcePages?: string | string[] | null
  sortOrder: number
  status: StationStatus
  tags: string | string[]
  subquestions?: CreateSubquestionInput[]
}

export interface UpdateStationInput {
  sectionId?: string
  title?: string
  scenario?: string
  type?: string
  source?: string | null
  sourcePages?: string | string[] | null
  sortOrder?: number
  status?: StationStatus
  tags?: string | string[]
}

export interface CreateSubquestionInput {
  stationId: string
  prompt: string
  questionType: string
  sortOrder: number
  marks?: number | null
  answers?: CreateAnswerInput[]
}

export interface UpdateSubquestionInput {
  stationId?: string
  prompt?: string
  questionType?: string
  sortOrder?: number
  marks?: number | null
}

export interface CreateAnswerInput {
  subquestionId: string
  content: string
  sortOrder: number
  isPrimary: boolean
}

export interface UpdateAnswerInput {
  subquestionId?: string
  content?: string
  sortOrder?: number
  isPrimary?: boolean
}

interface ApiErrorPayload {
  error?: unknown
  message?: string
  details?: unknown
}

export class AdminApiError extends Error {
  status: number
  code: string
  details?: unknown
  payload?: unknown

  constructor(message: string, status: number, code = 'request_error', details?: unknown, payload?: unknown) {
    super(message)
    this.name = 'AdminApiError'
    this.status = status
    this.code = code
    this.details = details
    this.payload = payload
  }
}

function toDisplayString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const text = value.trim()
  return text.length > 0 ? text : undefined
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeStationType(value: unknown): AdminStationType {
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.length > 0) return trimmed
  }
  return 'clinical'
}

function normalizeStationStatus(value: unknown): StationStatus {
  if (typeof value !== 'string') return 'draft'
  if (STATION_STATUSES.includes(value as StationStatus)) return value as StationStatus
  return 'draft'
}

function normalizeTags(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .filter((entry): entry is string => typeof entry === 'string')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .slice(0, 50)
  }
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed.startsWith('[')) {
      try {
        return normalizeTags(JSON.parse(trimmed))
      } catch {
        // Fall through to the comma-separated representation.
      }
    }
    return trimmed
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean)
      .slice(0, 50)
  }
  return []
}

function normalizeSourcePages(value: unknown): string | string[] | null {
  if (value === undefined || value === null || value === '') return null
  if (typeof value === 'string') return value
  if (Array.isArray(value)) {
    return value
      .map((entry) => (typeof entry === 'string' ? entry : String(entry)))
      .filter(Boolean)
      .slice(0, 50)
  }
  return String(value)
}

function mediaUrlForKey(key: string): string {
  return `/api/media/${key.split('/').map((segment) => encodeURIComponent(segment)).join('/')}`
}

function normalizeMedia(raw: unknown, stationId: string): AdminMedia | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const id = toDisplayString(data.id)
  const key = toDisplayString(data.key) ?? toDisplayString(data.objectKey) ?? toDisplayString(data.object_key)
  const altText = toDisplayString(data.altText) ?? toDisplayString(data.alt_text) ?? toDisplayString(data.alt) ?? ''
  if (!id || !key) return null
  const url = toDisplayString(data.url) ?? mediaUrlForKey(key)
  const filename = typeof data.filename === 'string' ? data.filename : null
  return {
    id,
    stationId: toDisplayString(data.stationId) ?? toDisplayString(data.station_id) ?? stationId,
    key,
    filename,
    mimeType: typeof data.mimeType === 'string'
      ? data.mimeType
      : typeof data.mime_type === 'string' ? data.mime_type : undefined,
    sizeBytes: Number.isFinite(Number(data.sizeBytes ?? data.size_bytes))
      ? Number(data.sizeBytes ?? data.size_bytes)
      : null,
    altText,
    alt: altText,
    sortOrder: toFiniteNumber(data.sortOrder ?? data.sort_order ?? data.order, 0),
    url,
  }
}

function normalizeAnswer(raw: unknown, fallbackSubquestionId = ''): AdminAnswer | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const id = toDisplayString(data.id)
  const subquestionId = toDisplayString(data.subquestionId) ?? toDisplayString(data.subquestion_id) ?? fallbackSubquestionId
  const body = toDisplayString(data.body) ?? toDisplayString(data.content) ?? toDisplayString(data.answer)
  if (!id || !subquestionId || !body) return null
  return {
    id,
    subquestionId,
    body,
    order: toFiniteNumber(data.order ?? data.sortOrder ?? data.sort_order, 0),
    sortOrder: toFiniteNumber(data.sortOrder ?? data.sort_order ?? data.order, 0),
    isPrimary: data.isPrimary === true || data.is_primary === true
      || data.isPrimary === 1 || data.is_primary === 1
      || data.isPrimary === '1' || data.is_primary === '1'
      || data.isPrimary === 'true' || data.is_primary === 'true',
  }
}

function normalizeSubquestion(raw: unknown): AdminSubquestion | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const id = toDisplayString(data.id)
  const stationId = toDisplayString(data.stationId) ?? toDisplayString(data.station_id)
  const prompt = toDisplayString(data.prompt) ?? toDisplayString(data.question)
  const questionType = toDisplayString(data.questionType) ?? toDisplayString(data.question_type) ?? 'text'
  if (!id || !stationId || !prompt) return null

  const answers = Array.isArray(data.answers)
    ? data.answers
        .map((item) => normalizeAnswer(item, stationId))
        .filter((answer): answer is AdminAnswer => Boolean(answer))
        .sort((left, right) => left.sortOrder - right.sortOrder)
    : []

  const order = toFiniteNumber(data.order ?? data.sortOrder ?? data.sort_order, 0)
  return {
    id,
    stationId,
    prompt,
    questionType,
    order,
    sortOrder: toFiniteNumber(data.sortOrder ?? data.sort_order, order),
    marks: data.marks === null || data.marks === undefined || data.marks === '' ? null : toFiniteNumber(data.marks, 0),
    answers,
  }
}

function normalizeStationSummary(raw: unknown): AdminStationSummary | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const id = toDisplayString(data.id)
  const sectionId = toDisplayString(data.sectionId) ?? toDisplayString(data.section_id)
  const title = toDisplayString(data.title)
  if (!id || !sectionId || !title) return null
  const status = normalizeStationStatus(data.status)
  return {
    id,
    sectionId,
    sectionName: toDisplayString(data.sectionName) ?? toDisplayString(data.section_name),
    sectionSlug: toDisplayString(data.sectionSlug) ?? toDisplayString(data.section_slug),
    title,
    scenario: toDisplayString(data.scenario),
    type: normalizeStationType(data.type),
    source: toDisplayString(data.source),
    sourcePages: normalizeSourcePages(data.sourcePages ?? data.source_pages),
    sortOrder: toFiniteNumber(data.sortOrder ?? data.sort_order ?? data.order, 0),
    order: toFiniteNumber(data.order ?? data.sortOrder ?? data.sort_order, 0),
    status,
    tags: normalizeTags(data.tags),
    questionCount: toFiniteNumber(data.questionCount ?? data.question_count ?? data.subquestionCount, 0),
    mediaCount: toFiniteNumber(data.mediaCount ?? data.media_count, 0),
  }
}

function normalizeStation(raw: unknown): AdminStation | null {
  if (!raw || typeof raw !== 'object') return null
  const base = normalizeStationSummary(raw)
  if (!base) return null
  const data = raw as Record<string, unknown>
  const station: AdminStation = {
    ...base,
    sectionId: base.sectionId,
    scenario: base.scenario ?? '',
    source: base.source ?? null,
    sourcePages: base.sourcePages ?? null,
    sortOrder: base.sortOrder,
    order: base.order ?? base.sortOrder,
    questionCount: base.questionCount,
    mediaCount: base.mediaCount,
    tags: base.tags,
    images: Array.isArray(data.images ?? data.media)
      ? (Array.isArray(data.images) ? data.images : data.media as unknown[])
          .map((item) => normalizeMedia(item, base.id))
          .filter((image): image is AdminMedia => Boolean(image))
      : [],
    subquestions: Array.isArray(data.subquestions ?? data.questions)
      ? (Array.isArray(data.subquestions) ? data.subquestions : data.questions as unknown[])
          .map(normalizeSubquestion)
          .filter((question): question is AdminSubquestion => Boolean(question))
          .sort((left, right) => left.sortOrder - right.sortOrder)
      : [],
  }

  return station
}

function normalizeSection(raw: unknown): Section | null {
  if (!raw || typeof raw !== 'object') return null
  const data = raw as Record<string, unknown>
  const id = toDisplayString(data.id)
  const name = toDisplayString(data.name) ?? toDisplayString(data.title) ?? 'قسم غير محدد'
  if (!id) return null
  return {
    id,
    name,
    slug: toDisplayString(data.slug) ?? id,
    order: toFiniteNumber(data.order ?? data.sortOrder ?? data.sort_order, 0),
    stationCount: Number(data.stationCount ?? data.station_count ?? 0),
  }
}

function normalizeSectionList(payload: unknown): Section[] {
  const data = payload && typeof payload === 'object' && !Array.isArray(payload)
    ? payload as { sections?: unknown; items?: unknown; data?: unknown }
    : undefined
  const nested = data?.data && typeof data.data === 'object' && !Array.isArray(data.data)
    ? data.data as { sections?: unknown; items?: unknown }
    : undefined
  const raw = Array.isArray(data?.sections)
    ? data.sections
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(nested?.sections)
        ? nested.sections
        : Array.isArray(nested?.items)
          ? nested.items
          : Array.isArray(payload) ? payload : []
  if (!Array.isArray(raw)) return []
  return raw
    .map(normalizeSection)
    .filter((section): section is Section => Boolean(section))
    .sort((left, right) => left.order - right.order)
}

function normalizeStationList(payload: unknown): AdminStationListResult {
  const data = (payload && typeof payload === 'object' && !Array.isArray(payload) ? payload : {}) as {
    stations?: unknown
    items?: unknown
    data?: unknown
    total?: unknown
    page?: unknown
    pageSize?: unknown
    hasMore?: unknown
  }

  const nested = data.data && typeof data.data === 'object' && !Array.isArray(data.data)
    ? data.data as { stations?: unknown; items?: unknown; total?: unknown; page?: unknown; pageSize?: unknown; hasMore?: unknown }
    : undefined
  const stationsSource = Array.isArray(data?.stations)
    ? data.stations
    : Array.isArray(data?.items)
      ? data.items
      : Array.isArray(nested?.stations)
        ? nested.stations
        : Array.isArray(nested?.items)
          ? nested.items
          : Array.isArray(payload) ? payload : []

  const stations = Array.isArray(stationsSource)
    ? stationsSource
        .map(normalizeStationSummary)
        .filter((station): station is AdminStationSummary => Boolean(station))
    : []

  return {
    stations,
    total: toFiniteNumber(data?.total ?? nested?.total, stations.length),
    page: toFiniteNumber(data?.page ?? nested?.page, 1),
    pageSize: toFiniteNumber(data?.pageSize ?? nested?.pageSize, stations.length),
    hasMore: Boolean(data?.hasMore ?? nested?.hasMore),
  }
}

function serializeError(status: number, raw: ApiErrorPayload): AdminApiError {
  const nestedError = raw && typeof raw.error === 'object' && !Array.isArray(raw.error)
    ? raw.error as ApiErrorPayload & { code?: unknown }
    : undefined
  const code = typeof raw.error === 'string'
    ? raw.error
    : typeof nestedError?.code === 'string' ? nestedError.code : 'request_error'
  const message = typeof raw.message === 'string'
    ? raw.message
    : typeof nestedError?.message === 'string'
      ? nestedError.message
      : `تعذر إكمال الطلب (رمز ${status})`
  return new AdminApiError(message, status, code, raw.details ?? nestedError?.details, raw)
}

function buildPath(path: string): string {
  return path.startsWith('/api/') ? path : `/api/${path}`
}

function encodeResourcePath(value: string): string {
  return value
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/')
}

function toBooleanQueryValue(value: boolean | undefined): string | undefined {
  return value ? '1' : undefined
}

function toQuery(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (typeof value === 'string' && value.length > 0) {
      search.set(key, value)
    }
  }
  return search.toString()
}

function safeTokenString(value: string | null | undefined): string | null {
  if (!value) return null
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

function ensureNumberOrFallback(value: unknown, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function normalizeIntOrUndefined(value: unknown, fallback?: number): number | undefined {
  if (value === undefined) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

async function parseResponseJson<T>(response: Response): Promise<T> {
  const raw = await response.text()
  if (!raw.trim()) return {} as T
  try {
    return JSON.parse(raw) as T
  } catch (error) {
    throw new AdminApiError('وصل رد غير صالح من الخادم.', response.status, 'invalid_json', error, raw)
  }
}

function buildRequestHeaders(init?: RequestInit): Headers {
  const headers = new Headers()
  if (init?.headers) {
    for (const [key, value] of new Headers(init.headers)) {
      headers.append(key, value)
    }
  }
  return headers
}

async function parseJsonOrThrow(response: Response): Promise<unknown> {
  const raw = await parseResponseJson<unknown>(response)
  if (response.ok) return raw
  if (typeof raw === 'object' && raw && !Array.isArray(raw)) {
    return serializeError(response.status, raw as ApiErrorPayload)
  }
  throw new AdminApiError('تعذر إكمال الطلب. حاول مرة أخرى.', response.status, 'request_error', raw, raw)
}

function unwrapPayload(value: unknown, key: string): unknown {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value
  const record = value as Record<string, unknown>
  if (record[key] !== undefined && record[key] !== null) return record[key]
  const data = record.data
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const nested = data as Record<string, unknown>
    if (nested[key] !== undefined && nested[key] !== null) return nested[key]
    return data
  }
  return value
}

export interface AdminApi {
  listSections(): Promise<AdminSectionResult>
  listStations(query?: ApiListQuery): Promise<AdminStationListResult>
  getStation(stationId: string): Promise<AdminStation>
  createStation(input: CreateStationInput): Promise<AdminStation>
  updateStation(stationId: string, input: UpdateStationInput): Promise<AdminStation>
  archiveStation(stationId: string): Promise<void>

  createSubquestion(input: CreateSubquestionInput): Promise<AdminSubquestion>
  updateSubquestion(subquestionId: string, input: UpdateSubquestionInput): Promise<AdminSubquestion>
  deleteSubquestion(subquestionId: string): Promise<void>

  createAnswer(input: CreateAnswerInput): Promise<AdminAnswer>
  updateAnswer(answerId: string, input: UpdateAnswerInput): Promise<AdminAnswer>
  deleteAnswer(answerId: string): Promise<void>

  uploadMedia(stationId: string, file: File, payload?: { altText?: string; sortOrder?: number }): Promise<AdminMedia>
  deleteMedia(rawKey: string): Promise<void>
}

export function createAdminApi(options: AdminApiOptions): AdminApi {
  const request = async <T>(path: string, init: RequestInit = {}): Promise<T> => {
    let token: string | null
    try {
      token = safeTokenString(await options.getToken())
    } catch (error) {
      throw new AdminApiError('تعذر قراءة جلسة الدخول. أعد تسجيل الدخول.', 401, 'token_error', error)
    }
    const localDevHost = typeof window !== 'undefined'
      && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    if (!token && !localDevHost) {
      throw new AdminApiError('سجّل الدخول أولًا للمتابعة.', 401, 'unauthenticated')
    }

    const headers = buildRequestHeaders(init)
    if (token) headers.set('Authorization', `Bearer ${token}`)
    else headers.set('x-dev-auth', 'admin')
    const isTextPayload = typeof init.body === 'string'
    if (isTextPayload && !headers.has('content-type')) {
      headers.set('content-type', 'application/json; charset=utf-8')
    }

    let response: Response
    try {
      response = await fetch(buildPath(path), {
        ...init,
        headers,
      })
    } catch (error) {
      throw new AdminApiError('تعذر الاتصال بالخادم. تحقق من الإنترنت وحاول مرة أخرى.', 0, 'network_error', error)
    }
    const parsed = await parseJsonOrThrow(response)
    if (!response.ok) {
      const error = parsed instanceof AdminApiError
        ? parsed
        : parsed as ApiErrorPayload
      if (error instanceof AdminApiError) throw error
      throw serializeError(response.status, error as ApiErrorPayload)
    }
    return parsed as T
  }

  const parseMaybeError = <T>(value: unknown): T => {
    if (value instanceof AdminApiError) throw value
    return value as T
  }

  const createHeadersPayload = <T>(value: T): string => JSON.stringify(value ?? {})

  return {
    listSections: async () => {
      const query = toQuery({ includeHidden: toBooleanQueryValue(true) })
      const data = await request<unknown>(`/api/sections?${query}`)
      return { sections: normalizeSectionList(data) }
    },
    listStations: async (query) => {
      const status = query?.status && query.status !== 'all' ? query.status : undefined
      const sectionId = query?.sectionId && query.sectionId !== 'all' ? query.sectionId : undefined
      const includeArchived = Boolean(query?.includeArchived)
      const params = toQuery({
        includeHidden: toBooleanQueryValue(true),
        includeArchived: toBooleanQueryValue(includeArchived),
        search: query?.search?.trim(),
        sectionId,
        status,
        page: query?.page ? String(query.page) : undefined,
        pageSize: query?.pageSize ? String(query.pageSize) : '24',
      })
      const response = await request<unknown>(`/api/stations?${params}`)
      return normalizeStationList(response)
    },
    getStation: async (stationId) => {
      const response = await request<unknown>(`/api/admin/stations/${encodeURIComponent(stationId)}`)
      const normalized = normalizeStation(unwrapPayload(response, 'station'))
      if (!normalized) throw new AdminApiError('تعذر قراءة محتوى المحطة.', 502, 'invalid_response')
      return normalized
    },
    createStation: async (input) => {
      const response = await request<unknown>('/api/admin/stations', {
        method: 'POST',
        body: createHeadersPayload(input),
      })
      const normalized = normalizeStation(unwrapPayload(response, 'station'))
      if (!normalized) throw new AdminApiError('تعذر قراءة استجابة إنشاء المحطة', 502, 'invalid_response')
      return normalized
    },
    updateStation: async (stationId, input) => {
      const response = await request<unknown>(`/api/admin/stations/${encodeURIComponent(stationId)}`, {
        method: 'PATCH',
        body: createHeadersPayload(input),
      })
      const normalized = normalizeStation(unwrapPayload(response, 'station'))
      if (!normalized) throw new AdminApiError('تعذر قراءة استجابة تعديل المحطة', 502, 'invalid_response')
      return normalized
    },
    archiveStation: async (stationId) => {
      await request<unknown>(`/api/admin/stations/${encodeURIComponent(stationId)}`, {
        method: 'DELETE',
      })
    },
    createSubquestion: async (input) => {
      const response = await request<unknown>('/api/admin/subquestions', {
        method: 'POST',
        body: createHeadersPayload(input),
      })
      const normalized = normalizeSubquestion(unwrapPayload(response, 'subquestion'))
      if (!normalized) throw new AdminApiError('تعذر قراءة استجابة إنشاء السؤال', 502, 'invalid_response')
      return normalized
    },
    updateSubquestion: async (subquestionId, input) => {
      const response = await request<unknown>(`/api/admin/subquestions/${encodeURIComponent(subquestionId)}`, {
        method: 'PATCH',
        body: createHeadersPayload(input),
      })
      const normalized = normalizeSubquestion(unwrapPayload(response, 'subquestion'))
      if (!normalized) throw new AdminApiError('تعذر قراءة استجابة تعديل السؤال', 502, 'invalid_response')
      return normalized
    },
    deleteSubquestion: async (subquestionId) => {
      await request<unknown>(`/api/admin/subquestions/${encodeURIComponent(subquestionId)}`, {
        method: 'DELETE',
      })
    },
    createAnswer: async (input) => {
      const response = await request<unknown>('/api/admin/answers', {
        method: 'POST',
        body: createHeadersPayload(input),
      })
      const normalized = normalizeAnswer(unwrapPayload(response, 'answer'), input.subquestionId)
      if (!normalized) throw new AdminApiError('تعذر قراءة استجابة إنشاء الإجابة', 502, 'invalid_response')
      return normalized
    },
    updateAnswer: async (answerId, input) => {
      const response = await request<unknown>(`/api/admin/answers/${encodeURIComponent(answerId)}`, {
        method: 'PATCH',
        body: createHeadersPayload(input),
      })
      const normalized = normalizeAnswer(unwrapPayload(response, 'answer'))
      if (!normalized) throw new AdminApiError('تعذر قراءة استجابة تعديل الإجابة', 502, 'invalid_response')
      return normalized
    },
    deleteAnswer: async (answerId) => {
      await request<unknown>(`/api/admin/answers/${encodeURIComponent(answerId)}`, {
        method: 'DELETE',
      })
    },
    uploadMedia: async (stationId, file, payload) => {
      const formData = new FormData()
      formData.append('stationId', stationId)
      formData.append('file', file)
      if (payload?.altText) formData.append('altText', payload.altText)
      if (payload?.sortOrder !== undefined) formData.append('sortOrder', String(payload.sortOrder))

      const response = await request<unknown>('/api/admin/media', {
        method: 'POST',
        body: formData as unknown as string,
      })
      const normalized = normalizeMedia(unwrapPayload(response, 'media'), stationId)
      if (!normalized) throw new AdminApiError('تعذر قراءة استجابة رفع الصورة', 502, 'invalid_response')
      return normalized
    },
    deleteMedia: async (rawKey) => {
      const encoded = encodeResourcePath(rawKey)
      await request<unknown>(`/api/admin/media/${encoded}`, {
        method: 'DELETE',
      })
    },
  }
}

export function formatStatusLabel(status: StationStatus): string {
  if (status === 'draft') return 'مسودة'
  if (status === 'needs_review') return 'بانتظار المراجعة'
  if (status === 'published') return 'منشورة'
  return 'مؤرشفة'
}

export function normalizeStationAnomaly(station: AdminStationSummary, sectionIds: Set<string>): boolean {
  // An empty section response can be a transient API failure; do not mark
  // every station anomalous until the section list has actually loaded.
  const missingSection = sectionIds.size > 0 && (!station.sectionId || !sectionIds.has(station.sectionId))
  const missingScenario = !station.scenario || station.scenario.trim().length < 20
  const noQuestions = Number(station.questionCount ?? 0) === 0
  const missingRequiredMedia = (station.type === 'image' || station.type === 'mixed') && Number(station.mediaCount ?? 0) === 0
  return missingSection || missingScenario || noQuestions || missingRequiredMedia
}

export function trimCommaSeparated(input: string): string[] {
  return input
    .split(',')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .slice(0, 50)
}

export function toSortOrderInput(value: string, fallback: number): number {
  const parsed = Number(value)
  if (!Number.isInteger(parsed) || parsed < 0) return fallback
  return parsed
}

export function normalizeStationForDraft(station: AdminStation): AdminStationSummary {
  return station
}

export function parseTagsFromInput(value: string): string {
  return trimCommaSeparated(value).join(',')
}

export function buildStationDraftPayload(draft: {
  sectionId: string
  title: string
  scenario: string
  type: string
  source: string
  sourcePages: string
  status: StationStatus
  sortOrder: number
  tags: string
}): CreateStationInput {
  return {
    sectionId: draft.sectionId.trim(),
    title: draft.title.trim(),
    scenario: draft.scenario.trim(),
    type: STATION_TYPES.includes(draft.type as StationType) ? (draft.type as StationType) : 'clinical',
    source: draft.source.trim(),
    sourcePages: draft.sourcePages.trim().length ? trimCommaSeparated(draft.sourcePages) : null,
    sortOrder: draft.sortOrder,
    status: draft.status,
    tags: parseTagsFromInput(draft.tags),
  }
}
